/**
 * Sentry Initialization Module
 * Story 8.1 Task 1: Install and Configure Sentry SDK
 *
 * Provides centralized Sentry configuration for error tracking
 *
 * @module lib/sentry
 */

import * as Sentry from '@sentry/react';

let initialized = false;

/**
 * Initialize Sentry error tracking
 *
 * Configures Sentry with:
 * - DSN from environment variable
 * - Environment detection (production/development)
 * - Release version tracking
 * - Error filtering for known noise
 *
 * @returns {void}
 */
export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  if (!dsn) {
    console.debug('[Sentry] DSN not configured, error tracking disabled');
    initialized = false;
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE, // 'development' | 'production'
    release: `genflappy@${import.meta.env.VITE_APP_VERSION || '0.0.0'}`,

    // Only send errors in production by default
    enabled: import.meta.env.PROD,

    // Reduce noise from known issues
    ignoreErrors: [
      // Browser extensions and ResizeObserver
      /^ResizeObserver loop/,
      // Network errors - handled by demo mode fallback
      /Failed to fetch/,
      // Chrome extensions
      /^chrome-extension/,
    ],

    beforeSend(event) {
      // Additional sanitization if needed in future
      return event;
    },
  });

  initialized = true;
}

/**
 * Check if Sentry has been successfully initialized
 *
 * @returns {boolean} True if Sentry is initialized and configured
 */
export function isSentryInitialized(): boolean {
  return initialized;
}

// Re-export Sentry functions for use elsewhere
export { Sentry };
